import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import useUI from 'src/hooks/use-ui';
import { useAddToCartModal } from 'components/modals/add-to-cart';
import { tracker } from 'src/utils/analytics';

import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import { useSponsoredProductHandlers } from 'src/utils/ads/hooks/sponsored-products';
import { SponsoredBrandCarouselV2 } from 'src/components/carousels/ads-carousels/sponsored-brand-carousel-v2';
import { CategoryPageRecommendations } from 'src/components/carousels/personalization/category-page-recommendations';
import FullCard from 'src/components/product-card-v2/full-card';
import { useGroupProductsByRows } from './use-group-products-by-rows';
import { getShowInterstitialCarousels } from './product-list.utils';
import { useProductListInjections } from './use-product-list-injections';

export const ROWS_FOR_ADS = [3, 5];

export default function ProductItemList({
  products,
  dispensary,
  trackerSource,
  adPlacement,
  productsCarouselQueries,
  hasPagination = false,
}) {
  const { iterables } = useCommerceComponents();
  const pageInterstitials = iterables.CategoryPageInterstitials();
  const { InterstitialComponent } = useProductListInjections({
    interstitials: pageInterstitials,
  });

  const { showSponsoredBrandsCarousel, showCategoryRecommendationsCarousel } = getShowInterstitialCarousels(
    productsCarouselQueries
  );
  const { sponsoredBrandQuery, personalizedProductsQuery } = productsCarouselQueries ?? {
    sponsoredBrandQuery: null,
    personalizedProductsQuery: null,
  };

  const UI = useUI();
  const { open: openAddToCartModal } = useAddToCartModal();

  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up(`md`));

  const { getItemsGroup } = useGroupProductsByRows({
    products,
    isListView: true,
    rows: ROWS_FOR_ADS,
  });

  const { handleSponsoredProductClick, handleSponsoredProductImpression } = useSponsoredProductHandlers({
    trackerSource,
    adPlacement,
  });

  const handleClick = useCallback(
    (product, index) => {
      tracker.setContext({ activeProductPosition: index });
      tracker.productClicked({
        product,
        position: index,
        trackerSource,
        placement: 'menu-page-product-item-list',
      });

      UI.activeProductSource = trackerSource;
      UI.setProductPosition({ productId: product.id, productIndex: index });

      handleSponsoredProductClick(product, index);
    },
    [UI, trackerSource, handleSponsoredProductClick]
  );

  const handleAddToCart = useCallback(
    (product, index, option) => {
      openAddToCartModal({
        quantity: 1,
        option,
        product,
        dispensary,
        trackerSource,
      });

      handleClick(product, index);
    },
    [dispensary, handleClick, openAddToCartModal, trackerSource]
  );

  const renderItem = (item, arrayIndex, { hideBorderOnFirst = false, showCarousel = false } = {}) => {
    if (item.type === 'slot') {
      const SlotComp = item.component;
      return <SlotComp key='slot-component' />;
    }

    const { product, index } = item;
    return (
      <Wrapper data-testid='product-list-item' key={product.id}>
        <FullCard
          product={product}
          productIndex={index}
          onClick={handleClick}
          onView={handleSponsoredProductImpression}
          onAddToCart={handleAddToCart}
          isMobile={!isDesktop}
          hideBorder={arrayIndex === 0 && hideBorderOnFirst && showCarousel}
        />
      </Wrapper>
    );
  };

  return (
    <Wrapper data-testid='product-list' $includeBottomBorder={hasPagination}>
      {getItemsGroup(0).map((item, idx) => renderItem(item, idx))}

      {showSponsoredBrandsCarousel && (
        <AdUnit data-testid='ad-unit-sponsored-brands-carousel'>
          <SponsoredBrandCarouselV2 sponsoredBrandQuery={sponsoredBrandQuery} adPlacement='category.carousel' />
        </AdUnit>
      )}

      {!showSponsoredBrandsCarousel && InterstitialComponent && (
        <div data-testid='product-list-interstitial-component'>
          <InterstitialComponent />
        </div>
      )}

      {getItemsGroup(1).map((item, idx) =>
        renderItem(item, idx, { hideBorderOnFirst: true, showCarousel: showSponsoredBrandsCarousel })
      )}

      {showCategoryRecommendationsCarousel && (
        <AdUnit data-testid='ad-unit-category-recommendations-carousel'>
          <CategoryPageRecommendations personalizedProductsQuery={personalizedProductsQuery} />
        </AdUnit>
      )}

      {getItemsGroup(2).map((item, idx) =>
        renderItem(item, idx, { hideBorderOnFirst: true, showCarousel: showCategoryRecommendationsCarousel })
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  ${({ $includeBottomBorder }) =>
    $includeBottomBorder &&
    css`
      border-bottom: 1px solid #e3e7e9;
    `}
`;

const AdUnit = styled.aside`
  @media (min-width: 960px) and (max-width: 1240px) {
    width: var(--kiosk-pro-content-width, calc(100vw - 270px));
  }

  &:last-child {
    border-top: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  }

  & > div {
    padding: 0;
  }
`;
